// Port of Flutter's mainScreen.dart — the "Your Locations" tab.
//
// A grid of location cards (maxCrossAxisExtent 230): ButtonColor card, radius
// 20, elevation 6, a 50px location_city icon over the location name. Tap opens
// the location's rooms; LONG-PRESS opens its address sheet. The assistance
// contact line is pinned to the bottom, and the FAB carries "Add Location".

import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardActionArea, Typography, CircularProgress, useTheme,
  Checkbox, FormControlLabel, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import CircleMenu from '../../components/CircleMenu';
import ExtentGrid from '../../components/ExtentGrid';
import useLongPress from '../../components/useLongPress';
import { useApp } from '../../../../core/state/AppContext';
import { useHideOffline } from '../../../../core/hooks/useHideOffline';
import { matchesTokens } from '../../../../core/utils/search';
import { arrayMove } from '../../../../core/utils/reorder';
import ReorderArrows, { EditOrderButton } from '../../components/ReorderBar';

function LocationCard({ location, onOpen, onEdit, reorder }) {
  const theme = useTheme();
  const c = theme.sli;
  const press = useLongPress(onEdit, onOpen);

  return (
    <Card elevation={6} sx={{ bgcolor: c.button, borderRadius: '20px', height: '100%', m: '5px' }}>
      <CardActionArea
        {...press}
        sx={{
          height: '100%', borderRadius: '20px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', p: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LocationCityIcon sx={{ color: c.text, fontSize: 50 }} />
          <Box sx={{ height: 16 }} />
          <Typography
            sx={{
              color: c.text,
              fontSize: 15,
              fontWeight: 700,
              textAlign: 'center',
              textShadow: '1px 1px 4px rgba(0,0,0,0.54)',
            }}
          >
            {location.LocationName}
          </Typography>
          {reorder && (
            <ReorderArrows
              index={reorder.index}
              count={reorder.count}
              onMove={reorder.onMove}
              color={c.text}
            />
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
}

export default function LocationsTab() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const {
    user, locations, locationsLoading, canReorder, editOrder, setEditOrder, reorderLocations,
  } = useApp();
  const [hideOffline, setHideOffline] = useHideOffline();

  const moveLocation = (i, dir) => {
    const j = i + dir;
    if (j >= 0 && j < locations.length) reorderLocations(arrayMove(locations, i, j));
  };

  // ── Search, because "All locations" turns four cards into 331 ─────────────
  // A grid is a fine way to show a household's handful of sites and a hopeless
  // one for the whole estate: no typing, just a very long scroll through cards
  // that mostly read "Home" or "Office". The desktop hit this first and
  // answered it with a type-to-search switcher in its header; the phone had no
  // equivalent, so All Locations was close to unusable there the moment it
  // existed.
  //
  // Matching is token-based (core/utils/search) for the same reason it is
  // everywhere else: the name may be stored "Sanjay Ailani-Home" and typed
  // "home sanjay", and a substring filter silently finds nothing for the order
  // that was not stored.
  const [query, setQuery] = useState('');
  const shown = useMemo(
    () => locations.filter((l) => matchesTokens(
      `${l.LocationName || ''} ${l.MasterUserName || ''}`, query,
    )),
    [locations, query],
  );

  // Only worth the vertical space once the list is long enough to need it — a
  // three-location household should not be asked to search three cards.
  const searchable = locations.length > 6 && !editOrder;

  // ⚠️ REORDERING ALWAYS SEES THE WHOLE LIST. The arrows and the SortOrder
  // writes address rows by index, so moving "the 3rd of 4 matches" would
  // persist position 3 of the FULL list — quietly reordering sites the operator
  // filtered out and cannot see. So the search is suspended while editing, the
  // same rule DesktopShell applies to its offline filter.
  const listed = editOrder ? locations : shown;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar
        title="Your Locations"
        actions={canReorder ? (
          <EditOrderButton
            editing={editOrder}
            onToggle={() => setEditOrder((v) => !v)}
            color={c.text}
          />
        ) : null}
      />

      {/* ── THE offline filter. One checkbox, for the whole app ──────────────
          It sits HERE, on the first screen, because this is the top of the only
          path there is: locations → rooms → devices. Ticking it applies to every
          list below it — each room's devices, the common strip, the sensors row
          and favourites — and to the desktop shell too, since both read one
          setting (core/hooks/useHideOffline).

          It used to be drawn on three separate screens, which read as three
          independent settings when it was always one; ticking it on one screen
          and finding another unchanged is what made it look broken. The other
          screens keep their "N hidden" counts — one control, but its effect has
          to be visible wherever it applies. */}
      <FormControlLabel
        sx={{ mx: 2, my: 0.25 }}
        control={(
          <Checkbox
            size="small"
            checked={hideOffline}
            onChange={(e) => setHideOffline(e.target.checked)}
            sx={{ color: c.text, '&.Mui-checked': { color: c.text } }}
          />
        )}
        label={(
          <Typography sx={{ color: c.text, fontSize: 13 }}>
            Hide offline devices
            <Typography component="span" sx={{ color: c.text, opacity: 0.6, fontSize: 11, ml: 0.75 }}>
              everywhere
            </Typography>
          </Typography>
        )}
      />

      {searchable && (
        <Box sx={{ mx: 2, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${locations.length} locations…`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: `${c.text}99`, fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                color: c.text,
                bgcolor: `${c.button}66`,
                '& fieldset': { borderColor: `${c.text}44` },
                '&.Mui-focused fieldset': { borderColor: c.text },
              },
              '& input::placeholder': { color: `${c.text}99`, opacity: 1 },
            }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative' }}>
        <Box sx={{ m: 2, mt: 0 }}>
          {locationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: c.text }} />
            </Box>
          ) : locations.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography sx={{ color: c.text, fontSize: 20 }}>No Locations Found</Typography>
            </Box>
          ) : listed.length === 0 ? (
            // Distinct from "No Locations Found" on purpose: the account HAS
            // locations, this search does not match any. Saying the same thing
            // for both reads as the list having gone missing.
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography sx={{ color: c.text, fontSize: 17 }}>
                {`No location matches “${query}”`}
              </Typography>
            </Box>
          ) : (
            <ExtentGrid maxExtent={230} childAspectRatio={1} gap={0}>
              {listed.map((l, i) => (
                <LocationCard
                  key={l.LocationID}
                  location={l}
                  onOpen={() => navigate(`/rooms/${l.LocationID}`, {
                    state: { locationName: l.LocationName },
                  })}
                  onEdit={() => navigate(`/location/${l.LocationID}`)}
                  // Index into the FULL list, which `listed` is while editing —
                  // see the note where `listed` is built.
                  reorder={editOrder
                    ? { index: i, count: listed.length, onMove: moveLocation }
                    : null}
                />
              ))}
            </ExtentGrid>
          )}
        </Box>

        {/* Pinned assistance line — Flutter aligns this to the bottom of the body. */}
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography sx={{ color: c.text }}>
            {`For Assistance Contact: ${user?.AssistanceContact ?? ''}`}
          </Typography>
        </Box>
      </Box>

      <CircleMenu
        items={[{
          icon: <AddLocationAltOutlinedIcon />,
          title: 'Add Location',
          onClick: () => navigate('/location/new'),
        }]}
      />
    </Box>
  );
}
